// client/src/components/InventoryItemDetails.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { InventoryItem, Site } from '../types/interfaces';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { paramFromSplatOrName } from '../utils/routeParams';
import { pickInventoryRecord } from '../utils/inventoryStock';
import { centralToday } from '../utils/centralDate';

interface InventoryProps {
  inventory: InventoryItem[];
  refreshInventory: () => Promise<void>;
}

interface HistoryRow {
  id: number;
  txnDate: string;
  txnType: string;
  txnTypeLabel: string;
  qty: number;
  locationName: string | null;
  customerName: string | null;
  notes: string | null;
  noteSummary: string | null;
  sourceRef: string | null;
  kegCount?: number;
  kegCountMismatch?: boolean;
  hasKegs?: boolean;
}

interface HistoryEvent {
  kind: string;
  label: string;
  href: string | null;
  found: boolean;
  invoiceId?: number | null;
  invoiceNumber?: string | null;
  transferId?: number | null;
  transferNumber?: string | null;
  batchId?: string | null;
  sourceRef?: string | null;
}

interface HistoryDetail extends HistoryRow {
  event: HistoryEvent;
  kegCodes: string[];
  kegCodesLinked: boolean;
  kegMatch?: string | null;
  qtyExpected?: number | null;
}

const InventoryItemDetails: React.FC<InventoryProps> = ({ inventory, refreshInventory }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const siteIdParam = searchParams.get('siteId') || '';
  const locationIdParam = searchParams.get('locationId') || '';
  const hasAccountParam = searchParams.has('account');
  const accountParam = searchParams.get('account') || '';
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [adjustForm, setAdjustForm] = useState<{ newQuantity: string; reason: string; date: string }>({
    newQuantity: '',
    reason: '',
    date: centralToday(),
  });
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [productionError, setProductionError] = useState<string | null>(null);
  const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [selectedTxnId, setSelectedTxnId] = useState<number | null>(null);
  const [historyDetail, setHistoryDetail] = useState<HistoryDetail | null>(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [historyDetailError, setHistoryDetailError] = useState<string | null>(null);
  const HISTORY_PAGE_SIZE = 50;
  const getIdentifier = (item: InventoryItem) => item.identifier || 'N/A';

  // Splat captures names with `/` (e.g. 1/6 BBL Keg). Legacy `_`→`/` encoding still accepted.
  const rawParam = paramFromSplatOrName(params, 'identifier');
  const decodedIdentifier = rawParam.includes('/') ? rawParam : rawParam.replace(/_/g, '/');

  // Helper function to get site name
  const getSiteName = (siteId: string | undefined) => {
    if (!siteId) return 'N/A';
    const site = sites.find((s) => s.siteId === siteId);
    return site ? site.name : 'N/A';
  };

  const zeroStockItem = useCallback((): InventoryItem => ({
    identifier: decodedIdentifier,
    item: decodedIdentifier,
    type: 'Finished Goods' as InventoryItem['type'],
    quantity: '0',
    unit: 'ea' as InventoryItem['unit'],
    receivedDate: '',
    siteId: siteIdParam,
    locationId: locationIdParam ? Number(locationIdParam) : undefined,
    account: hasAccountParam ? (accountParam as InventoryItem['account']) : undefined,
    status: 'Stored' as InventoryItem['status'],
    source: 'ledger',
  }), [decodedIdentifier, siteIdParam, locationIdParam, hasAccountParam, accountParam]);

  const pickItem = useCallback(
    (items: InventoryItem[]): InventoryItem | null =>
      pickInventoryRecord(items, {
        identifier: decodedIdentifier,
        siteId: siteIdParam,
        locationId: locationIdParam,
        account: accountParam,
        hasAccount: hasAccountParam,
      }),
    [decodedIdentifier, siteIdParam, locationIdParam, accountParam, hasAccountParam]
  );

  useEffect(() => {
    const apply = (found: InventoryItem | null) => {
      const next = found || zeroStockItem();
      setItem(next);
      setAdjustForm((prev) => ({ ...prev, newQuantity: next.quantity || '0' }));
      setProductionError(null);
    };
    const fetchItem = async () => {
      if (!decodedIdentifier) {
        setProductionError('Missing inventory identifier in URL');
        return;
      }
      const fromProps = pickItem(inventory || []);
      if (fromProps) {
        apply(fromProps);
        return;
      }
      try {
        const res = await apiFetch(
          `${API_BASE_URL}/api/inventory?includeZero=1&extrasOnly=1`,
          {
            headers: { Accept: 'application/json' },
            allowHttpError: true,
          }
        );
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        const items: InventoryItem[] = Array.isArray(data) ? data : data.items || [];
        const foundItem = pickItem(items);
        if (foundItem) {
          console.log('[InventoryItemDetails] Fetched item:', foundItem);
        }
        // A zero-stock location may have no inventory row. Still open History.
        apply(foundItem);
      } catch (err: any) {
        console.error('[InventoryItemDetails] Fetch item error:', err);
        apply(null);
      }
    };
    void fetchItem();
  }, [decodedIdentifier, inventory, pickItem, zeroStockItem]);

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/sites`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data: Site[] = await res.json();
        console.log('[InventoryItemDetails] Fetched sites:', data);
        setSites(data);
      } catch (err: any) {
        console.error('[InventoryItemDetails] Fetch sites error:', err);
        setProductionError('Failed to fetch sites: ' + err.message);
      }
    };
    fetchSites();
  }, [API_BASE_URL]);

  const loadHistory = useCallback(async (identifier: string, siteId?: string, offset = 0) => {
    const appending = offset > 0;
    if (appending) setHistoryLoadingMore(true);
    else {
      setHistoryLoading(true);
      setHistoryError(null);
      setSelectedTxnId(null);
      setHistoryDetail(null);
      setHistoryDetailError(null);
    }
    try {
      const qs = new URLSearchParams({
        itemName: identifier,
        limit: String(HISTORY_PAGE_SIZE),
        offset: String(offset),
      });
      if (siteId) qs.set('siteId', siteId);
      const res = await apiFetch(`${API_BASE_URL}/api/inventory/history?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
        allowHttpError: true,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.slice(0, 120) || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const rows: HistoryRow[] = Array.isArray(data.rows) ? data.rows : [];
      setHistoryRows((prev) => (appending ? [...prev, ...rows] : rows));
      setHistoryHasMore(!!data.hasMore);
    } catch (err: any) {
      console.error('[InventoryItemDetails] History load error:', err);
      if (!appending) setHistoryRows([]);
      setHistoryError('Failed to load history: ' + (err.message || 'unknown error'));
    } finally {
      setHistoryLoading(false);
      setHistoryLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!item) return;
    const id = getIdentifier(item);
    if (!id || id === 'N/A') return;
    void loadHistory(id, item.siteId, 0);
  }, [item, loadHistory]);

  const openKegHistory = (code: string) => {
    navigate(`/keg-tracking?keg=${encodeURIComponent(code)}`);
  };

  const handleHistoryRowClick = async (txnId: number) => {
    if (selectedTxnId === txnId) {
      setSelectedTxnId(null);
      setHistoryDetail(null);
      setHistoryDetailError(null);
      return;
    }
    setSelectedTxnId(txnId);
    setHistoryDetail(null);
    setHistoryDetailError(null);
    setHistoryDetailLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/inventory/history/${txnId}`, {
        headers: { Accept: 'application/json' },
        allowHttpError: true,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.slice(0, 120) || `HTTP ${res.status}`);
      }
      const data: HistoryDetail = await res.json();
      setHistoryDetail(data);
    } catch (err: any) {
      console.error('[InventoryItemDetails] History detail error:', err);
      setHistoryDetailError('Failed to load event details: ' + (err.message || 'unknown error'));
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  const openLinkedEvent = () => {
    const href = historyDetail?.event?.href;
    if (href) navigate(href);
  };

  const linkedEventButtonLabel = (event: HistoryEvent | undefined) => {
    if (!event?.href) return null;
    if (event.kind === 'invoice') return 'Open Invoice';
    if (event.kind === 'transfer') return 'Open Transfer';
    if (event.kind === 'batch') return 'Open Batch';
    return 'Open Linked Event';
  };

  const handleAdjustChange = (field: keyof typeof adjustForm, value: string) => {
    setAdjustForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdjustSubmit = async () => {
    if (!item || !adjustForm.newQuantity || isNaN(parseFloat(adjustForm.newQuantity)) || parseFloat(adjustForm.newQuantity) < 0 || !adjustForm.reason) {
      setProductionError('Valid new quantity and reason are required');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/inventory/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          identifier: getIdentifier(item),
          newQuantity: adjustForm.newQuantity,
          reason: adjustForm.reason,
          date: adjustForm.date,
          siteId: item.siteId,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP error! status: ${res.status}, ${text.slice(0, 50)}`);
      }
      const updatedItem = await res.json();
      console.log('[InventoryItemDetails] Adjusted item:', updatedItem);
      setItem((prev) => (prev ? { ...prev, quantity: updatedItem.newQuantity, totalCost: updatedItem.newTotalCost } : null));
      setShowAdjustForm(false);
      setProductionError(null);
      await refreshInventory();
    } catch (err: any) {
      console.error('[InventoryItemDetails] Adjust inventory error:', err);
      setProductionError('Failed to adjust item: ' + err.message);
    }
  };

  console.log('[InventoryItemDetails] Render:', {
    identifier: decodedIdentifier,
    item: item ? getIdentifier(item) : null,
    showAdjustForm,
    productionError,
    isMobile: window.innerWidth <= 768,
  });

  if (!item && !productionError) return <div className="alert alert-info text-center">Loading...</div>;
  if (productionError) return <div className="alert alert-danger text-center">{productionError}</div>;

  return (
    <div className="page-container container">
      <div className="inventory-item-details-header">
        <h2 className="app-header mb-0">Inventory Item Details</h2>
        <div className="inventory-actions mb-0">
          <button
            type="button"
            onClick={() => setShowAdjustForm(!showAdjustForm)}
            className="btn btn-primary"
            data-testid="adjust-inventory-button"
          >
            {showAdjustForm ? 'Cancel Adjustment' : 'Adjust Inventory'}
          </button>
        </div>
      </div>
      {showAdjustForm && item && (
        <div className="adjust-form mb-3">
          <h3 className="app-header mb-3">Adjust Inventory</h3>
          <div className="mb-3"><strong>Current Quantity:</strong> {item.quantity} {item.unit}</div>
          <div className="mb-3"><strong>Current Total Cost:</strong> ${parseFloat(item.totalCost || '0').toFixed(2)}</div>
          <label className="form-label">
            New Quantity:
            <input
              type="number"
              value={adjustForm.newQuantity}
              onChange={(e) => handleAdjustChange('newQuantity', e.target.value)}
              step="0.01"
              className="form-control"
            />
          </label>
          <label className="form-label">
            Reason:
            <select
              value={adjustForm.reason}
              onChange={(e) => handleAdjustChange('reason', e.target.value)}
              className="form-control"
            >
              <option value="">Select Reason</option>
              <option value="Spillage">Spillage</option>
              <option value="Error">Error</option>
              <option value="Breakage">Breakage</option>
              <option value="Destroyed">Destroyed</option>
            </select>
          </label>
          <label className="form-label">
            Date:
            <input
              type="date"
              value={adjustForm.date}
              onChange={(e) => handleAdjustChange('date', e.target.value)}
              className="form-control"
            />
          </label>
          <button onClick={handleAdjustSubmit} className="btn btn-primary mt-3">
            Save Adjustment
          </button>
          {productionError && <div className="alert alert-danger mt-3">{productionError}</div>}
        </div>
      )}
      <div className="batch-details inventory-item-details">
        <div><strong>Item-Lot:</strong> {item ? getIdentifier(item) : 'N/A'}</div>
        <div><strong>Type:</strong> {item?.type}</div>
        <div><strong>Description:</strong> {item?.description || 'N/A'}</div>
        <div><strong>Quantity:</strong> {item?.quantity} {item?.unit}</div>
        <div><strong>Unit:</strong> {item?.unit || 'N/A'}</div>
        {item?.type === 'Spirits' && <div><strong>Proof:</strong> {item?.proof || 'N/A'}</div>}
        {item?.type === 'Spirits' && <div><strong>Proof Gallons:</strong> {item?.proofGallons || 'N/A'}</div>}
        <div><strong>Total Cost:</strong> {item?.totalCost ? `$${parseFloat(item.totalCost).toFixed(2)}` : 'N/A'}</div>
        <div><strong>Date Received:</strong> {item?.receivedDate || 'N/A'}</div>
        <div><strong>Source:</strong> {item?.source || 'N/A'}</div>
        <div><strong>Location:</strong> {item?.account || 'Storage'}</div>
        <div><strong>Site:</strong> {item ? getSiteName(item.siteId) : 'N/A'}</div>
        <div><strong>Status:</strong> {item?.status || 'Stored'}</div>
        <div><strong>DSP Number:</strong> {item?.dspNumber || 'N/A'}</div>
        <section className="inventory-history mt-4" aria-labelledby="inventory-history-heading">
          <h3 id="inventory-history-heading" className="app-header mb-3">
            History
          </h3>
          <p className="text-muted small mb-2">
            Ledger events for this item (newest first). Rows with a keg badge show linked assets —
            tap or click the row to expand keg chips. Chips open that keg&apos;s history when a
            confident match exists (invoice, batch+date, transfer TO#, or unambiguous
            product+date+location).
          </p>
          {historyLoading && <div className="alert alert-info">Loading history…</div>}
          {historyError && <div className="alert alert-warning">{historyError}</div>}
          {!historyLoading && !historyError && historyRows.length === 0 && (
            <div className="alert alert-secondary">No ledger history for this item yet.</div>
          )}
          {historyRows.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm table-hover inventory-history-table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Event</th>
                    <th scope="col">Qty</th>
                    <th scope="col">Location</th>
                    <th scope="col">Note / Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => {
                    const expanded = selectedTxnId === row.id;
                    const showKegBadge = (row.kegCount ?? 0) > 0 || !!row.hasKegs;
                    const kegLabel =
                      (row.kegCount ?? 0) === 1
                        ? '1 keg'
                        : `${row.kegCount ?? 0} kegs`;
                    return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={[
                          'inventory-history-row',
                          expanded ? 'table-active' : '',
                          showKegBadge ? 'inventory-history-row--has-kegs' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ cursor: 'pointer' }}
                        onClick={() => void handleHistoryRowClick(row.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void handleHistoryRowClick(row.id);
                          }
                        }}
                        tabIndex={0}
                        aria-expanded={expanded}
                        aria-label={
                          showKegBadge
                            ? `History ${row.txnTypeLabel} on ${row.txnDate}, ${kegLabel}. ${
                                expanded ? 'Collapse' : 'Expand'
                              } to show keg codes`
                            : `History ${row.txnTypeLabel} on ${row.txnDate}`
                        }
                      >
                        <td>
                          <span
                            className="inventory-history-expand"
                            aria-hidden="true"
                            title={expanded ? 'Collapse' : 'Expand for details'}
                          >
                            {expanded ? '▾' : '▸'}
                          </span>
                          {row.txnDate || '—'}
                        </td>
                        <td>
                          <span className="inventory-history-event-cell">
                            {row.txnTypeLabel || row.txnType}
                            {showKegBadge && (
                              <span
                                className={
                                  row.kegCountMismatch
                                    ? 'inventory-history-keg-badge inventory-history-keg-badge--mismatch'
                                    : 'inventory-history-keg-badge'
                                }
                                data-testid={`history-keg-badge-${row.id}`}
                                title={
                                  row.kegCountMismatch
                                    ? `Matched ${row.kegCount} kegs (does not match ledger qty ${row.qty}) — tap to expand`
                                    : 'Tap row to expand keg codes'
                                }
                              >
                                {kegLabel}
                                {row.kegCountMismatch ? ' ⚠' : ''}
                              </span>
                            )}
                          </span>
                        </td>
                        <td>{row.qty}</td>
                        <td>{row.locationName || '—'}</td>
                        <td>{row.noteSummary || '—'}</td>
                      </tr>
                      {expanded && (
                        <tr className="inventory-history-detail-row">
                          <td colSpan={5}>
                            {historyDetailLoading && <div>Loading event details…</div>}
                            {historyDetailError && (
                              <div className="alert alert-warning mb-0">{historyDetailError}</div>
                            )}
                            {historyDetail && !historyDetailLoading && (
                              <div className="inventory-history-detail">
                                <div>
                                  <strong>Event:</strong>{' '}
                                  {historyDetail.event?.label || historyDetail.txnTypeLabel}
                                </div>
                                <div>
                                  <strong>Source ref:</strong>{' '}
                                  {historyDetail.sourceRef ||
                                    historyDetail.event?.invoiceNumber ||
                                    historyDetail.event?.transferNumber ||
                                    historyDetail.event?.batchId ||
                                    '—'}
                                </div>
                                {!historyDetail.event?.href && historyDetail.event?.kind === 'batch' && (
                                  <div className="text-muted">
                                    Batch not loaded in Tilly yet. Packaging from import still shows
                                    here; full Ekos batch records are not imported. Source ref above
                                    is the batch id when known.
                                  </div>
                                )}
                                {!historyDetail.event?.href && historyDetail.event?.kind !== 'batch' && (
                                  <div className="text-muted">
                                    Linked source document is not openable in Tilly yet (common for
                                    imported history). Event type and source ref above still identify
                                    the ledger row.
                                  </div>
                                )}
                                <div className="inventory-history-kegs mt-2">
                                  <strong>Kegs in this event:</strong>
                                  {historyDetail.kegCountMismatch && (
                                    <div
                                      className="alert alert-warning py-1 px-2 mt-1 mb-1 small"
                                      data-testid="inventory-history-keg-mismatch"
                                    >
                                      Matched {historyDetail.kegCodes.length} keg
                                      {historyDetail.kegCodes.length === 1 ? '' : 's'}
                                      {historyDetail.qtyExpected != null
                                        ? ` (ledger qty ${Math.abs(Number(historyDetail.qtyExpected))})`
                                        : ''}
                                      . Showing matches only — not padded.
                                    </div>
                                  )}
                                  {historyDetail.kegCodesLinked && historyDetail.kegCodes.length > 0 ? (
                                    <div
                                      className="inventory-history-keg-chips"
                                      data-testid="inventory-history-keg-chips"
                                    >
                                      {historyDetail.kegCodes.map((code) => (
                                        <button
                                          key={code}
                                          type="button"
                                          className="inventory-history-keg-chip"
                                          data-testid={`keg-chip-${code}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openKegHistory(code);
                                          }}
                                        >
                                          {code}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-muted ms-1">No keg assets linked</span>
                                  )}
                                </div>
                                <div className="inventory-history-detail-actions mt-2">
                                  {linkedEventButtonLabel(historyDetail.event) && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary me-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openLinkedEvent();
                                      }}
                                    >
                                      {linkedEventButtonLabel(historyDetail.event)}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedTxnId(null);
                                      setHistoryDetail(null);
                                      setHistoryDetailError(null);
                                    }}
                                  >
                                    Close Event Details
                                  </button>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {historyHasMore && item && (
                <div className="text-center mt-2 mb-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    data-testid="inventory-history-load-more"
                    disabled={historyLoadingMore}
                    onClick={() =>
                      void loadHistory(getIdentifier(item), item.siteId, historyRows.length)
                    }
                  >
                    {historyLoadingMore ? 'Loading…' : 'Load more history'}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        <div className="inventory-actions mt-4">
          <button
            type="button"
            onClick={() => navigate('/inventory')}
            className="btn btn-danger"
          >
            Back to Inventory
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryItemDetails;